<template>
  <div class="sales page page-with-table p-2">
    <page-header class="mb-2" :title="$t('Live Lot Performance Sales')">
    </page-header>
    <div class="card content-wrapper p-2">
      <div class="slaes-table table-component">
        <artisio-table :pagination="tableData.pagination"
                       :sort="tableData.ordering"
                       :items="tableData.rows"
                       :busy="tableData.loading"
                       :fields="tableFields"
                       :checkboxes="true"
                       :enable-simple-search="false"
                       show-toggle-columns
                       export-vuex-module-name="sales"
                       export-permission-name="export_auction"
                       :extra-data="toggleColumns"
                       @paramsChanged="getTableData"
                       @on-column-save="updateToggleColumns">
          <template slot="cell(sale_no)" slot-scope="{item: sale}">
            <router-link :to="{name: 'liveLotsPerformance', params: {auctionUuid: sale.uuid}}">
              {{ sale.sale_no }}
            </router-link>
          </template>
          <template slot="cell(images)" slot-scope="{item: sale}">

          </template>

          <template slot="cell(title)" slot-scope="{item: sale}">
            <router-link :to="{name: 'liveLotsPerformance', params: {auctionUuid: sale.uuid}}">
              {{ sale.title | masterLanguage | striptags }}
            </router-link>
          </template>

          <template slot="cell(start_date)" slot-scope="{item: sale}">
            {{ sale.start_date | toDateTime }}
          </template>

          <template slot="cell(end_date)" slot-scope="{item: sale}">
            {{ sale.end_date | toDateTime }}
          </template>

          <template slot="cell(total_bidders)" slot-scope="{item: sale}">
            {{ sale.total_bidders }}
          </template>

          <template slot="cell(total_amount_over_reserve)" slot-scope="{item: sale}">
            {{sale.total_count_over_reserve}} {{ $t('lots') }} / {{ sale.total_amount_over_reserve | asCurrency(getCurrency(sale)) }}
          </template>

          <template slot="cell(total_amount_bellow_reserve)" slot-scope="{item: sale}">
            {{sale.total_count_bellow_reserve}} {{ $t('lots') }} /  {{ sale.total_amount_bellow_reserve | asCurrency(getCurrency(sale)) }}
          </template>

          <template slot="cell(department)" slot-scope="{item: sale}">
            <template v-if="sale.department">
              <router-link v-if="$hasPermission('setup')"
                           :to="{name: 'departments', params: {department_uuid: sale.department_uuid}}">
                {{ sale.department.name | masterLanguage }}
              </router-link>
              <span v-else>{{ sale.department.name | masterLanguage }}</span>
            </template>
          </template>

          <template slot="cell(currency)" slot-scope="{item}">
            {{ item.currency.code }}
          </template>

          <template slot="cell(llp)" slot-scope="{item: sale}">
            <router-link :to="{name: 'liveLotsPerformance', params: {auctionUuid: sale.uuid}}"
                         class="btn btn-sm btn-primary">
              {{ $t('Open Sale LLP') }}
            </router-link>
          </template>

          <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                    v-for="dynamicField in toggleColumns.dynamicFields">
            <div
                v-html="$options.filters.asDynamicField(
                $options.filters.masterLanguage(item.dynamic_fields)[dynamicField],
                field.type,
                field.choices
              )">
            </div>
          </template>

        </artisio-table>
      </div>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
import PageHeader from "@/core/components/page-header/PageHeader.vue";
import {ARTISIO_IDLE_TRACKER_KEY} from "@/modules/constants";

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('sales');
const {mapState: globalMapState} = createNamespacedHelpers('global');

export default {
  name: "LLPSales",
  components: {PageHeader, ArtisioTable},
  props: {},
  data: () => {
    return {
      loading: false,
      rows: [],
      pagination: {
        limit: 200,
        page: 1,
        total: 0,
      },
      autoUpdateInterval: null,
      idleTimeout: 60 * 60 * 1000
    }
  },
  computed: {
    ...globalMapState(['enableTimedSessions', 'enableContinuousAuction']),
    ...mapState({
      tableData: state => state.tableData[state.tableSource],
      tableSource: state => state.tableSource
    }),
    ...mapGetters({
      tableFields: 'tableFields',
      toggleColumns: 'toggleColumns',
    }),
  },
  methods: {
    ...mapActions(['getTableData', 'updateToggleColumns', 'updateTableSource']),
    async onViewClick(data) {
      this.$router.push({
        name: 'sales.view',
        params: {id: data.uuid}
      });
    },
    getCurrency(sale) {
      return sale.currency ? sale.currency.symbol : null;
    },
    startInterval(withoutLoader = true) {
      this.getTableData({withoutLoader: withoutLoader})
      this.autoUpdateInterval = setInterval(async () => {
        let lastInteractionTime = localStorage.getItem(ARTISIO_IDLE_TRACKER_KEY)

        if (lastInteractionTime) {
          lastInteractionTime = parseInt(lastInteractionTime)

          if (Date.now() - lastInteractionTime >= this.idleTimeout) {
            clearInterval(this.autoUpdateInterval)
            let success = await this.$confirm(this.$i18n.t("Are you still here using the page?"));
            if (success) {
              this.startInterval(true);
            }
          } else {
            this.getTableData({withoutLoader: true})
          }
        }
      }, 30000);
    }
  },
  async beforeMount() {
    if (this.enableContinuousAuction) {
      this.$router.push({
        name: 'liveLotsPerformance'
      })
    } else {
      this.updateTableSource({
        source: 'llp'
      });

      this.startInterval(false)
    }

  },
  beforeDestroy() {
    clearInterval(this.autoUpdateInterval)
  }
}
</script>

<style lang="scss" scoped>

.artisio-table {
  /deep/ {
    .col-saleno {
      min-width: 160px;
    }

    .status-col {
      min-width: 90px;
    }

    .start-date-col {
      min-width: 150px;
    }

    .title-col {
      min-width: 150px;
    }

    .department-col {
      min-width: 140px;
    }

    .currency-col {
      min-width: 95px;
    }

    .items-col {
      min-width: 80px;
    }

    .type-col {
      min-width: 100px;
    }

    .live-reference-col {
      min-width: 140px;
    }

    .premiums-col {
      min-width: 100px;
    }

    .payment-terms-col {
      min-width: 100px;
    }

    .settlement-days-col {
      min-width: 100px;
    }

    .reserved-items-col {
      min-width: 100px;
    }

    .total-lots-col {
      min-width: 100px;
    }

    .estimate-col {
      min-width: 100px;
    }

    .reserve-col {
      min-width: 100px;
    }

    .sold-lots-col {
      min-width: 100px;
    }

    .unsold-lots-col {
      min-width: 100px;
    }

    .created-data-col {
      min-width: 100px;
    }

    .updated-data-col {
      min-width: 100px;
    }
  }
}
</style>

